import { FC, useCallback, useMemo } from "react";
import classNames from "classnames";
import { Button } from "../../../Button";
import { ActionProps } from "../Action";
import { BaseProps } from "../../../../BaseProps";
import { DataId } from "../../../DataId";
import { AccessibilityProps } from "../../../../AccessibilityProps";

/**
 * The `ActionButton` component properties.
 */
export interface ActionButtonProps extends ActionProps, BaseProps, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The row ID.
   */
  readonly id: DataId;
}

/**
 * A single action button.
 */
export const ActionButton: FC<ActionButtonProps> = (props) => {
  const { id, icon, color = "body", disabled = false, tooltip, onClick, testId, "aria-label": ariaLabel } = props;

  const isDisabled = useMemo(() => {
    return typeof disabled === "boolean" ? disabled : disabled?.(id) ?? false;
  }, [disabled, id]);

  const clickHandler = useCallback(() => {
    onClick?.(id);
  }, [id, onClick]);

  return (
    <Button
      className={classNames("action", color)}
      icon={typeof icon === "function" ? icon(id) : icon}
      appearance="text"
      size="small"
      disabled={isDisabled}
      onClick={clickHandler}
      tooltip={typeof tooltip === "function" ? tooltip(id) : tooltip}
      testId={testId}
      aria-label={ariaLabel ?? "action button"}
    />
  );
};
